import { Rect, Size2, Vector2 } from "peet-math";

export class ViewPort {
  public viewPortTag: JQuery<HTMLElement>;
  public viewRatio: number;
  public planRatio: number;
  public sizeOnScreen: Size2;
  public planSize: Size2;
  public planRect: Rect;
  public size: Size2;
  public p1: Vector2;
  public p2: Vector2;
  public rect: Rect;
  public rectOnScreen: Rect;
  public zoom: number;
  public maxZoom: number;
  public minZoom: number;
  public idealSkyZoom: number;
  public center: Vector2;
  public mousePos: Vector2;
  
  constructor(widthOnScreen, heightOnScreen) {
    // Init
    this.setViewScreenSize = this.setViewScreenSize.bind(this);
    this.setPlanSize = this.setPlanSize.bind(this);
    this._refreshZoomMaximums = this._refreshZoomMaximums.bind(this);
    this.getRestrictedZoom = this.getRestrictedZoom.bind(this);
    this.calc = this.calc.bind(this);
    this.scalarPToV = this.scalarPToV.bind(this);
    this.scalarVToP = this.scalarVToP.bind(this);
    this.pointPToV = this.pointPToV.bind(this);
    this.pointVToP = this.pointVToP.bind(this);
    this.vectorPToV = this.vectorPToV.bind(this);
    this.vectorVToP = this.vectorVToP.bind(this);
    this.sizePToV = this.sizePToV.bind(this);
    this.sizeVToP = this.sizeVToP.bind(this);
    this.setViewScreenSize(widthOnScreen, heightOnScreen);
    this.center = new Vector2(0, 0);
    this.mousePos = null; // This is null when we don't know the mouse position over the plan
    this.planSize = null;
    this.zoom = 1; // 1 is the 1:1 zoom, lim(->0) is the highest zoom,
    this.maxZoom = 4;
    this.minZoom = 0.1; // This is a default but it's recalculated with the plan size later
    this.idealSkyZoom = this.minZoom; // This is calculated higher that min zoom just for the fun of the effect in sky moves.
    this.calc();
  }
    
  setViewScreenSize(width, height) {
    this.sizeOnScreen = new Size2(width, height);
    this.viewRatio = this.sizeOnScreen.width / this.sizeOnScreen.height;
    return this._refreshZoomMaximums();
  }
    
  setPlanSize(width, height) {
    this.planSize = new Size2(width, height);
    this.planRect = new Rect(0, 0, width, height);
    this.planRatio = this.planSize.width / this.planSize.height;
    return this._refreshZoomMaximums();
  }
    
  _refreshZoomMaximums() {
    if ((this.planSize != null) && (this.sizeOnScreen != null)) {
      if (this.planRatio > this.viewRatio)
        this.minZoom = this.sizeOnScreen.width / this.planSize.width; // For instance 0.56
      else
        this.minZoom = this.sizeOnScreen.height / this.planSize.height; // For instance 0.56
    
      // Clip the zoom in the case the view port is larger than the plan.
      if(this.minZoom > 1)
        this.minZoom = 1;
      // Calculate the ideal sky zoom
      // To make it cool, the sky zoom is higher than the minZoom but obviously, it must not be 0!
      // We tkae the normal zoom of 1, we subtract the min zoom so we are higher and we divide this result to locate ourself between the min zoom and 0.
      return this.idealSkyZoom = this.minZoom / 2;
    }
  }

  getRestrictedZoom(desiredZoom: number): number {
    if (desiredZoom < this.minZoom) {
      return this.minZoom;
    } else if (desiredZoom > this.maxZoom) {
      return this.maxZoom;
    } else {
      return desiredZoom;
    }
  }

  
  calc() {
    // Convert to PU
    this.size = this.sizeVToP(this.sizeOnScreen); // Viewport size on the plan
    this.p1 = this.center.sub(this.size.scale(1 / 2)); // Center - half the size
    this.p2 = this.p1.add(this.size); // P1 + the size of the thing
    this.rect = Rect.rectFromPoints(this.p1, this.p2); // Rect on plan
    return this.rectOnScreen = new Rect(0, 0, this.sizeOnScreen.width, this.sizeOnScreen.height);
  }

  // Conversion P: plan, V: ViewPort
  scalarPToV(scalar) { return scalar * this.zoom; }
  //scalarVToP: (scalar) => scalar / @zoom
  scalarVToP(scalar) { return scalar / this.zoom; }
  pointPToV(point) { return point.sub(this.p1).scale(this.zoom); }
  pointVToP(point) { return point.scale(1 / this.zoom).add(this.p1); }
  vectorPToV(size) { return size.scale(this.zoom); }
  vectorVToP(size) { return size.scale(1 / this.zoom); }
  sizePToV(size) { return size.scale(this.zoom); }
  sizeVToP(size) { return size.scale(1 / this.zoom); }
}
